import React, { createContext, useContext, useMemo } from "react";
import { makeMoney } from "./format";

/**
 * The currency every amount on the Player Insights tab is expressed in.
 *
 * UPS converts all of a player's money into one reporting currency (their
 * account currency, else the brand default) and returns it as major units
 * with `currency_code` / `currency_precision`. The tab must never assume a
 * currency of its own — the accepted design's "KES everywhere" was true of
 * its Kenyan sample player, not of the platform, which runs brands in USDT,
 * KES, USD and others.
 *
 * Provided once at the tab root so every card formats the same way; sections
 * read it with useMoney() rather than being handed a currency prop apiece.
 * The default mirrors the dashboards, which label money with the brand
 * currency saved at login (see Currency() in sharedfiles/helper.js), so a
 * profile that somehow lacks a currency still shows the brand's rather than
 * nothing.
 */
const brandCurrency = () => {
  try {
    return localStorage.getItem("currency") || "";
  } catch (e) {
    return "";
  }
};

const CurrencyContext = createContext(makeMoney(brandCurrency(), 2));

export function CurrencyProvider({ code, precision, children }) {
  const value = useMemo(
    () => makeMoney(code || brandCurrency(), precision === null || precision === undefined ? 2 : precision),
    [code, precision]
  );
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

/** { code, symbol, precision, amount, money, moneyExact } — see makeMoney in ./format. */
export const useMoney = () => useContext(CurrencyContext);
